'use client';
import React from 'react';

interface BlogPost {
  category: string;
  title: string;
  excerpt: string;
  image: string;
  alt: string;
  date: string;
  readTime: string;
  featured?: boolean;
}

const blogPosts: BlogPost[] = [
{
  category: 'Jewish Heritage',
  title: 'The Complete Guide to Jewish Heritage Sites in Morocco',
  excerpt:
  'Morocco is home to one of the world\'s most remarkable Jewish heritage trails. From the ancient synagogues of Fes to the mellahs of Marrakech, discover the extraordinary legacy of 2,500 years of Jewish life.',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_199c4afd2-1783465024295.png',
  alt: 'Historic Jewish synagogue in Morocco with ornate Moorish architecture and Star of David decorations',
  date: 'March 15, 2024',
  readTime: '8 min read',
  featured: true
},
{
  category: 'Morocco Travel Guide',
  title: 'The Ultimate Morocco Travel Guide for First-Time Visitors',
  excerpt:
  'Planning your first trip to Morocco? This comprehensive guide covers everything you need to know — from the best time to visit and what to pack, to navigating the medinas and experiencing authentic Moroccan culture.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_12830db1a-1772461174895.png",
  alt: 'Marrakech medina with traditional Moroccan architecture and colorful souks at golden hour',
  date: 'February 28, 2024',
  readTime: '12 min read'
},
{
  category: 'Imperial Cities',
  title: "Morocco's Four Imperial Cities: A Journey Through History",
  excerpt:
  'Fes, Marrakech, Meknes, and Rabat — Morocco\'s four imperial cities each offer a unique window into the country\'s magnificent history. Discover what makes each city extraordinary.',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_171138ac6-1767962895334.png',
  alt: 'Fes ancient medina with historic imperial architecture and traditional Moroccan buildings',
  date: 'February 10, 2024',
  readTime: '10 min read'
},
{
  category: 'Cruise Excursions',
  title: 'Best Shore Excursions from Casablanca: A Complete Guide',
  excerpt:
  'Docking in Casablanca? Make the most of your port stop with these expertly curated shore excursions. From the Hassan II Mosque to the historic Mellah, discover the best of Morocco in a day.',
  image: "https://images.unsplash.com/photo-1696711092118-3eaa2890bbad",
  alt: 'Casablanca port with cruise ship and Hassan II Mosque visible in the background',
  date: 'January 22, 2024',
  readTime: '7 min read'
},
{
  category: 'Luxury Travel',
  title: 'Why Private Tours Are the Only Way to Experience Morocco',
  excerpt:
  'Group tours miss the magic. Discover why discerning travelers choose private tours for their Morocco experience — and how a personalized journey transforms your understanding of this extraordinary country.',
  image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80',
  alt: 'Luxury private tour vehicle in Morocco with Atlas Mountains in the background',
  date: 'January 8, 2024',
  readTime: '6 min read'
},
{
  category: 'History',
  title: 'Tracing Your Moroccan Jewish Roots: A Genealogy Travel Guide',
  excerpt:
  'Thousands of Jewish families around the world have roots in Morocco. This guide explains how to research your Moroccan Jewish ancestry and plan a meaningful genealogy journey to discover your heritage.',
  image: "https://images.unsplash.com/photo-1576268365549-4f8366d3ef2f",
  alt: 'Historic Jewish cemetery in Morocco with ancient tombstones and Hebrew inscriptions',
  date: 'December 15, 2023',
  readTime: '9 min read'
}];


const blogCategories = [
'All',
'Jewish Heritage',
'Morocco Travel Guide',
'Imperial Cities',
'Cruise Excursions',
'Luxury Travel',
'History',
'Travel Tips'];


export default function Blog() {
  const handleNavClick = (href: string) => {
    const el = document.querySelector(href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const featured = blogPosts[0];
  const rest = blogPosts.slice(1);

  return (
    <section id="blog" className="section-padding bg-white">
      <div className="container-luxury">
        {/* Header */}
        <div className="text-center mb-12 animate-on-scroll">
          <span className="tag-gold">Blog & Travel Guides</span>
          <h2 className="section-title mt-4 mb-4">
            Morocco Travel
            <br />
            <span className="text-gold italic">Insights & Stories</span>
          </h2>
          <div className="gold-divider" />
          <p className="section-subtitle mx-auto mt-4">
            Expert travel guides, Jewish heritage insights, and Morocco travel tips from David&apos;s
            years of experience guiding visitors across the kingdom.
          </p>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap justify-center gap-2 mb-10 animate-on-scroll">
          {blogCategories.map((cat) =>
          <button
            key={cat}
            className="font-sans text-xs font-bold uppercase tracking-wider px-4 py-2 border border-gray-200 text-navy hover:border-gold hover:text-gold transition-all duration-300">
            
              {cat}
            </button>
          )}
        </div>

        {/* Featured Post */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 mb-10 animate-on-scroll shadow-luxury">
          <div className="relative overflow-hidden h-72 lg:h-auto">
            <img
              src={featured.image}
              alt={featured.alt}
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              loading="lazy" />
            
            <div className="absolute top-4 left-4">
              <span className="font-sans text-xs font-bold uppercase tracking-wider px-3 py-1 bg-gold text-navy-dark">
                Featured
              </span>
            </div>
          </div>
          <div className="bg-cream p-8 md:p-12 flex flex-col justify-center">
            <span className="tag-gold mb-4">{featured.category}</span>
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-navy mb-4 leading-tight">
              {featured.title}
            </h3>
            <div className="gold-divider-left" />
            <p className="font-sans text-sm text-gray-600 leading-relaxed mb-6 mt-4">
              {featured.excerpt}
            </p>
            <div className="flex items-center gap-4 text-xs text-gray-500 font-sans mb-6">
              <span>{featured.date}</span>
              <span>·</span>
              <span>{featured.readTime}</span>
            </div>
            <button
              onClick={() => handleNavClick('#contact')}
              className="btn-gold self-start">
              
              Read More
            </button>
          </div>
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {rest.map((post, index) =>
          <article
            key={post.title}
            className="blog-card animate-on-scroll"
            style={{ transitionDelay: `${index % 3 * 100}ms` }}>
            
              <div className="relative h-52 overflow-hidden">
                <img
                src={post.image}
                alt={post.alt}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy" />
              
                <div className="absolute top-4 left-4">
                  <span className="font-sans text-xs font-bold uppercase tracking-wider px-3 py-1 bg-gold text-navy-dark">
                    {post.category}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 text-xs text-gray-400 font-sans mb-3">
                  <span>{post.date}</span>
                  <span>·</span>
                  <span>{post.readTime}</span>
                </div>
                <h3 className="font-serif text-lg font-bold text-navy mb-3 leading-tight group-hover:text-gold transition-colors duration-300">
                  {post.title}
                </h3>
                <p className="font-sans text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                <button className="font-sans text-xs font-bold text-gold uppercase tracking-wider flex items-center gap-1 group-hover:gap-2 transition-all duration-300">
                  Read Article
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </article>
          )}
        </div>

        {/* Newsletter CTA */}
        <div className="mt-16 bg-navy p-8 md:p-12 animate-on-scroll">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <span className="tag-gold mb-4 inline-block">Stay Informed</span>
              <h3 className="font-serif text-2xl font-bold text-white mt-3 mb-3">
                Morocco Travel Tips & Jewish Heritage Insights
              </h3>
              <p className="font-sans text-white/70 text-sm">
                Get exclusive travel guides, Jewish heritage stories, and Morocco travel tips
                delivered directly from David.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                placeholder="Your email address"
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 text-white placeholder-white/50 font-sans text-sm focus:outline-none focus:border-gold" />
              
              <button className="btn-gold whitespace-nowrap">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>);

}